import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Button,
  ButtonGroup,
  FieldSet,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Tag,
  TextButton,
} from '@zillow/constellation-10';

import Modal from '@common/components/ConstellationV10Modal';
import userPersonas from '@common/constants/userPersonas';
import type { ILeasesForPropertyModel } from '@rental-manager/models/Types';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

type AdditionalLeaseSelectorProps = {
  index: number | null;
};

type LeaseSelectorProps = ILeasesForPropertyModel & AdditionalLeaseSelectorProps;

type Props = {
  currentProperty: ZrmClickstreamListing;
  leasesForProperty: Array<LeaseSelectorProps>;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (leaseId: number | null) => void;
  propertyId: string;
  user: ZrmClickstreamUser;
};

const LeaseSelectorPage = ({
  currentProperty,
  leasesForProperty,
  onBack,
  onClose,
  onSubmit,
  propertyId,
  user,
}: Props) => {
  // @ts-ignore
  const leaseId = useSelector((state) => state.payments.landlord.onboarding[propertyId]?.leaseId);
  const [selectedLease, setSelectedLease] = useState<number | null>(null);

  useEffect(() => {
    if (leaseId !== null) {
      setSelectedLease(leaseId);
    }
  }, [leaseId]);

  const leases: Array<LeaseSelectorProps> = leasesForProperty.map((lease, index) => ({
    ...lease,
    index,
  }));

  const handleSubmit = () => {
    onSubmit(selectedLease);
    IreneOnboardingCSEvents.emitContinueWithLeaseSelectedEvent({
      currentProperty,
      user,
    });
  };

  const onSkip = () => {
    onSubmit(null);
    IreneOnboardingCSEvents.emitContinueFromLeaseSelectorToAddPaymentManuallyEvent({
      currentProperty,
      user,
    });
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header="Payment setup"
      onRequestClose={onClose}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <FieldSet
            legend={
              <Legend css={{ textStyle: 'body-lg-bold' }}>Select the lease you’re setting up payments for</Legend>
            }
          >
            {(leases || []).map((lease) => {
              return (
                <LabeledControl
                  key={lease.index}
                  control={
                    <Radio
                      name="lease-selection"
                      checked={selectedLease === lease.index}
                      onChange={() => setSelectedLease(lease.index)}
                    />
                  }
                  label={
                    <Label>
                      {`${lease.leaseTenants[0].firstName} ${lease.leaseTenants[0].lastName}${lease.leaseTenants[0].lastName.endsWith('s') ? '’' : '’s'} Lease`}
                      <Tag size="sm" css={{ marginLeft: 'tighter', padding: 'tightest' }} tone="green">
                        {lease.status}
                      </Tag>
                    </Label>
                  }
                />
              );
            })}
          </FieldSet>
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Lease selection actions">
          <TextButton onClick={onBack}>Back</TextButton>
          <TextButton onClick={onSkip}>I don’t have a lease in Zillow/Set up manually</TextButton>
          <Button onClick={handleSubmit} disabled={selectedLease === null} emphasis="filled">
            Continue
          </Button>
        </ButtonGroup>
      }
    />
  );
};

export default LeaseSelectorPage;
